import {
  experimentSubjectKeySchema,
  experimentAssignmentSchema,
  type ExperimentAssignment,
  type ExperimentManifest,
  type ExperimentSubjectKey,
  type ExperimentVariant,
} from './schema';

export interface ExperimentAssignmentContext {
  subjectKey: string;
  platform?: 'ios' | 'android' | 'web' | 'api';
  locale?: string;
  segmentKeys?: readonly string[];
  internalCohorts?: readonly string[];
}

export function resolveExperimentAssignment(
  manifest: ExperimentManifest,
  context: ExperimentAssignmentContext,
  now: Date = new Date(),
): ExperimentAssignment | null {
  if (manifest.status !== 'running') return null;
  if (manifest.targeting.rollout_percent <= 0) return null;
  if (!isEligible(manifest, context)) return null;

  const subjectKey = toExperimentSubjectKey(context.subjectKey);
  const override = manifest.targeting.subject_overrides?.[subjectKey];
  const overrideVariant = override
    ? manifest.variants.find((variant) => variant.key === override)
    : undefined;
  if (overrideVariant) {
    return buildAssignment(manifest, overrideVariant, subjectKey, 0, now, context);
  }

  const bucket = stableBucket(
    `${manifest.experiment_key}:${manifest.version}:${manifest.targeting.salt}:${subjectKey}`,
  );
  if (bucket >= manifest.targeting.rollout_percent * 100) return null;

  const variantBucket = stableBucket(
    `${manifest.experiment_key}:${manifest.version}:${manifest.targeting.salt}:${subjectKey}:variant`,
  );
  const variant = pickVariant(manifest.variants, variantBucket);
  return buildAssignment(manifest, variant, subjectKey, bucket, now, context);
}

export function toExperimentSubjectKey(subjectKey: string): ExperimentSubjectKey {
  const parsed = experimentSubjectKeySchema.safeParse(subjectKey);
  if (parsed.success) return parsed.data;

  return experimentSubjectKeySchema.parse(`subject_${stableHash(subjectKey).toString(16)}`);
}

function isEligible(manifest: ExperimentManifest, context: ExperimentAssignmentContext): boolean {
  if (manifest.targeting.rollout_percent <= 0) return false;
  return isAudienceEligible(manifest, context);
}

/**
 * The audience half of eligibility — platform/locale/segment/cohort rules —
 * WITHOUT the rollout gate. The remote (posthog-source) resolver consumes this
 * directly: audience targeting is always server-owned (ADR-015), while rollout
 * for a posthog-source manifest is runtime-authoritative in the console, so the
 * committed percent must not gate remote enrollment.
 */
export function isAudienceEligible(
  manifest: ExperimentManifest,
  context: ExperimentAssignmentContext,
): boolean {
  const { targeting } = manifest;
  if (
    targeting.platforms?.length &&
    (!context.platform || !targeting.platforms.includes(context.platform))
  ) {
    return false;
  }
  if (
    targeting.locales?.length &&
    (!context.locale || !targeting.locales.includes(context.locale))
  ) {
    return false;
  }
  if (
    targeting.segment_keys?.length &&
    !targeting.segment_keys.some((segment) => context.segmentKeys?.includes(segment))
  ) {
    return false;
  }
  if (
    targeting.internal_cohorts?.length &&
    !targeting.internal_cohorts.some((cohort) => context.internalCohorts?.includes(cohort))
  ) {
    return false;
  }
  return true;
}

function pickVariant(variants: readonly ExperimentVariant[], bucket: number): ExperimentVariant {
  const variantBucket = bucket % 10_000;
  let cursor = 0;
  for (const variant of variants) {
    cursor += variant.allocation_percent * 100;
    if (variantBucket < cursor) return variant;
  }
  const fallback = variants.at(-1);
  if (!fallback) {
    throw new Error('experiment variants must contain at least one variant');
  }
  return fallback;
}

/**
 * Builds the canonical assignment record. Exported for the remote resolver so
 * a posthog-source assignment is byte-compatible with a local one — same
 * deterministic `assignment_id` recipe, same schema parse.
 */
export function buildAssignment(
  manifest: ExperimentManifest,
  variant: ExperimentVariant,
  subjectKey: string,
  bucket: number,
  now: Date,
  context?: ExperimentAssignmentContext,
): ExperimentAssignment {
  return experimentAssignmentSchema.parse({
    assignment_id: `exp_${stableHash(
      `${manifest.experiment_key}:${manifest.version}:${variant.key}:${subjectKey}`,
    ).toString(16)}`,
    experiment_key: manifest.experiment_key,
    experiment_version: manifest.version,
    assignment_source: manifest.assignment_source,
    flag_key: manifest.flag_key,
    variant_key: variant.key,
    subject_key: subjectKey,
    bucket,
    assigned_at: now.toISOString(),
    ...(context?.platform ? { client_platform: context.platform } : {}),
    ...(context?.locale ? { locale: context.locale } : {}),
    targets: variant.targets,
  });
}

/** Exported for the remote resolver's informational bucket continuity. */
export function stableBucket(input: string): number {
  return stableHash(input) % 10_000;
}

function stableHash(input: string): number {
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
